import { useState, useEffect } from "react";

/**
 * Hook to track unread message count from Crisp chat
 * Uses the Crisp SDK's message:received event to update count
 */
export function useCrispUnreadCount(): number {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const crisp = (window as any).$crisp;
    
    if (!crisp) {
      // Crisp not loaded yet, try again later
      const checkInterval = setInterval(() => {
        const crispCheck = (window as any).$crisp;
        if (crispCheck) {
          setupListeners(crispCheck);
          clearInterval(checkInterval);
        }
      }, 1000);

      // Clean up interval after 10 seconds if Crisp never loads
      setTimeout(() => clearInterval(checkInterval), 10000);

      return () => clearInterval(checkInterval);
    } else {
      setupListeners(crisp);
    }

    function setupListeners(crispInstance: any) {
      // Get initial unread count
      try {
        const count = crispInstance.get("chat:unread:count") || 0;
        setUnreadCount(count);
      } catch {
        // Ignore errors
      }

      // Listen for new messages
      crispInstance.push(["on", "message:received", () => {
        try {
          const count = crispInstance.get("chat:unread:count") || 0;
          setUnreadCount(count);
        } catch {
          // Fallback: increment by 1
          setUnreadCount(prev => prev + 1);
        }
      }]);

      // Listen for chat opened (reset count when user views messages)
      crispInstance.push(["on", "chat:opened", () => {
        setUnreadCount(0);
      }]);

      // Listen for message read events
      crispInstance.push(["on", "message:read", () => {
        setUnreadCount(0);
      }]);
    }

    return () => {
      // Crisp doesn't have a clean "off" API, but component unmount is fine
    };
  }, []);

  return unreadCount;
}
